import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';
import LabeledInput from '../../../../../../components/LabeledInput';
import { Button, spaceMixin, Spacer, VisuallyHidden } from '@zillow/constellation';
import styled from 'styled-components';
import { INVALID_EMAIL_MSG, INVALID_NAME_MSG } from '../../../../../../constants/messages';
import { useDispatch, useSelector } from 'react-redux';
import { isZtssSelector, leadInfoSelector } from '../../../../../../selectors';
import { markRentalTermsAccepted } from '../../../../../../actions/rentalTermsActionCreators';
import { sendApplicationInvitation } from '../../../../../../actions/invitationActionCreators';
import useQueryVariables from '../../../../../../hooks/useQueryVariables';
import UtmGuids from '../../../../../../constants/utmGuids';
import { useZgGraphqlApolloClient } from '../../../../../../contexts/ZgGraphContext';
import { LISTING_APPLICATIONS_QUERY } from '../../../../../../constants/queries';

const SendInvitationButton = styled(Button)`
  display: block;
  font-weight: 700;
`;

const NameWrapper = styled.fieldset`
  display: flex;
  padding: 0; /* Overriding MUI styling */
  & ${LabeledInput} {
    flex: 0 1 50%;
    &:not(:last-child) {
      margin-right: ${spaceMixin('sm')};
    }
  }
`;

function useSendInvitation(onSubmit) {
  const { propertyId } = useQueryVariables();
  const dispatch = useDispatch();
  const isZtss = useSelector(isZtssSelector);
  const zgGraphqlApolloClient = useZgGraphqlApolloClient();

  return useCallback(
    async (values) => {
      const payload = {
        listingId: propertyId,
        ...values,
      };
      if (isZtss) {
        const utm_source = localStorage?.getItem('sia_utm_source') ?? '';
        const utm_content = localStorage?.getItem('sia_utm_content') ?? '';
        payload.utmGuid = `${UtmGuids.siaExperianReferral}${utm_source}${utm_content}`;
      }
      dispatch(markRentalTermsAccepted());
      onSubmit(values);
      await dispatch(sendApplicationInvitation(payload));
      zgGraphqlApolloClient.refetchQueries({
        include: [LISTING_APPLICATIONS_QUERY],
      });
    },
    [propertyId, isZtss, dispatch, onSubmit, zgGraphqlApolloClient],
  );
}

export default function InvitationForm({ onSubmit, ...rest }) {
  const initialValues = useSelector(leadInfoSelector);
  const sendInvitation = useSendInvitation(onSubmit);
  return (
    <Form
      onSubmit={sendInvitation}
      initialValues={initialValues}
      render={({ handleSubmit, submitting, invalid, form }) => {
        return (
          <Spacer
            as="form"
            onSubmit={async (e) => {
              await handleSubmit(e);
              form.restart();
            }}
            {...rest}
          >
            <Field
              autoFocus
              required
              name="toEmail"
              id="to-email"
              label="Email"
              validate={(value) => (/(.+?)@(.+?)\.[a-zA-Z]{1,}/.test(value) ? null : INVALID_EMAIL_MSG)}
              component={LabeledInput}
              marginBottom="xs"
            />
            <NameWrapper>
              <VisuallyHidden as="legend">Name</VisuallyHidden>
              <Field
                name="renterFirstName"
                id="renter-first-name"
                label="First name"
                inputClassName="fs-exclude"
                validate={(value) => (!value || value.length < 33 ? null : INVALID_NAME_MSG)}
                component={LabeledInput}
              />
              <Field
                name="renterLastName"
                id="renter-last-name"
                label="Last name"
                inputClassName="fs-exclude"
                validate={(value) => (!value || value.length < 33 ? null : INVALID_NAME_MSG)}
                component={LabeledInput}
              />
            </NameWrapper>
            <SendInvitationButton
              buttonType="primary"
              marginTop="sm"
              marginBottom="xs"
              id="send-invitation-button"
              type="submit"
              disabled={invalid || submitting}
              fluid
            >
              Send
            </SendInvitationButton>
          </Spacer>
        );
      }}
    />
  );
}

InvitationForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};
